"use client";

import { CheckCircle2 } from "lucide-react";

import { useCheckout } from "@/lib/hooks/use-checkout";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

const PLANS = [
  {
    productId: "starter" as const,
    name: "Starter",
    price: "£14.99",
    period: "/month",
    description: "For solo creators getting started.",
    features: [
      "2 live funnels",
      "Visual block editor",
      "Custom subdomain",
      "Lead capture",
    ],
    cta: "Start monthly",
  },
  {
    productId: "lifetime" as const,
    name: "Lifetime",
    price: "£67",
    originalPrice: "£420",
    period: "once",
    description: "Pay once. Own it forever.",
    badge: "Best value · Save 84%",
    features: [
      "Everything in Growth",
      "10 live funnels",
      "All future updates",
      "No recurring fees, ever",
      "Locked-in early-bird price",
    ],
    cta: "Get Lifetime — £67",
    highlighted: true,
  },
  {
    productId: "growth" as const,
    name: "Growth",
    price: "£34.99",
    period: "/month",
    description: "For agencies & growing creators.",
    features: [
      "10 live funnels",
      "Everything in Starter",
      "Priority support",
      "Advanced analytics",
    ],
    cta: "Start monthly",
  },
];

export function PricingTrio() {
  const { loadingPlan, error, start } = useCheckout("landing_pricing");

  return (
    <section
      id="pricing"
      className="bg-white px-6 py-20 sm:py-28"
    >
      <div className="mx-auto max-w-6xl">
        <div className="mx-auto max-w-2xl text-center">
          <p
            data-reveal
            className="text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-600"
          >
            Pricing
          </p>
          <h2
            data-reveal
            className="mt-4 font-heading text-[32px] font-bold leading-[0.95] tracking-[-0.02em] text-slate-900 sm:text-[44px]"
          >
            <span className="text-gradient-brand">Pay once, own it forever</span>
            <span> — or pick monthly if you&apos;d rather start small.</span>
          </h2>
        </div>

        <div
          data-reveal-stagger
          className="mt-14 grid grid-cols-1 items-stretch gap-4 lg:grid-cols-[1fr_1.18fr_1fr]"
        >
          {PLANS.map((plan) => {
            const highlighted = "highlighted" in plan && plan.highlighted;
            return highlighted ? (
              <HighlightedPlanCard
                key={plan.productId}
                plan={plan as HighlightedPlan}
                loading={loadingPlan === plan.productId}
                disabled={loadingPlan !== null}
                onSelect={() => start(plan.productId)}
              />
            ) : (
              <PlanCard
                key={plan.productId}
                plan={plan}
                loading={loadingPlan === plan.productId}
                disabled={loadingPlan !== null}
                onSelect={() => start(plan.productId)}
              />
            );
          })}
        </div>

        {error && (
          <p className="mt-6 text-center text-sm text-rose-500">{error}</p>
        )}

        <p className="mt-10 text-center text-[12px] text-slate-500">
          All plans include unlimited funnel drafts, our 5 design templates,
          and 30-day refund window. VAT may apply by region.
        </p>
      </div>
    </section>
  );
}

interface BasePlan {
  productId: "starter" | "growth" | "lifetime";
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
}

interface HighlightedPlan extends BasePlan {
  badge: string;
  originalPrice: string;
  highlighted: true;
}

interface CardProps<P extends BasePlan> {
  plan: P;
  loading: boolean;
  disabled: boolean;
  onSelect: () => void;
}

function PlanCard({ plan, loading, disabled, onSelect }: CardProps<BasePlan>) {
  return (
    <div className="relative flex flex-col rounded-2xl border border-slate-200/80 bg-white p-7 shadow-sm sm:p-8">
      <p className="font-heading text-[13px] font-bold uppercase tracking-[0.16em] text-slate-500">
        {plan.name}
      </p>
      <div className="mt-4 flex items-baseline gap-1.5">
        <span className="font-heading text-[40px] font-extrabold tracking-tight text-slate-900">
          {plan.price}
        </span>
        <span className="text-sm font-medium text-slate-500">
          {plan.period}
        </span>
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-slate-600">
        {plan.description}
      </p>

      <ul className="mt-6 flex flex-1 flex-col gap-2.5">
        {plan.features.map((f) => (
          <li
            key={f}
            className="flex items-start gap-2 text-[13.5px] leading-snug text-slate-700"
          >
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            {f}
          </li>
        ))}
      </ul>

      <button
        onClick={onSelect}
        disabled={disabled}
        className="mt-7 flex h-11 items-center justify-center rounded-full border border-slate-200 bg-white text-[13.5px] font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <Spinner className="h-3.5 w-3.5" />
            Redirecting…
          </span>
        ) : (
          plan.cta
        )}
      </button>
    </div>
  );
}

function HighlightedPlanCard({
  plan,
  loading,
  disabled,
  onSelect,
}: CardProps<HighlightedPlan>) {
  return (
    <div className="relative lg:scale-[1.04]">
      {/* Badge sits in this wrapper (no overflow clipping) */}
      <span className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-white px-3.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-violet-700 shadow-[0_6px_20px_-6px_rgba(91,33,182,0.55)] ring-1 ring-violet-200">
        {plan.badge}
      </span>

      <div
        className={cn(
          "relative flex h-full flex-col overflow-hidden rounded-2xl p-7 sm:p-8",
          // Brand-palette background: blue → purple → red, with the orange
          // arriving as a corner glow below. Stops chosen to keep white
          // text legible across the surface.
          "bg-[linear-gradient(155deg,#1A4FCF_0%,#5C2EB8_45%,#7A35BC_72%,#B33A2A_100%)]",
          "shadow-[0_30px_70px_-25px_rgba(91,33,182,0.6)]",
          "ring-1 ring-violet-400/40",
        )}
      >
        {/* decorative grid */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(0deg, rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage:
              "radial-gradient(circle at 70% 0%, black 0%, transparent 65%)",
            WebkitMaskImage:
              "radial-gradient(circle at 70% 0%, black 0%, transparent 65%)",
          }}
        />
        {/* Orange corner glow — the missing palette stop, dropped in as a
            decorative bloom so the full logo spectrum is present without
            sacrificing text contrast. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[#F29120]/35 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-20 -bottom-20 h-56 w-56 rounded-full bg-[#1463F0]/30 blur-3xl"
        />

        <div className="relative">
          <p className="font-heading text-[13px] font-bold uppercase tracking-[0.16em] text-violet-200">
            {plan.name}
          </p>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="text-base font-medium text-violet-200/80 line-through">
              {plan.originalPrice}
            </span>
            <span className="font-heading text-[56px] font-extrabold leading-none tracking-tight text-white">
              {plan.price}
            </span>
            <span className="text-sm font-medium text-violet-200/90">
              {plan.period}
            </span>
          </div>
          <p className="mt-2 text-[13px] leading-relaxed text-violet-100/90">
            {plan.description}
          </p>
        </div>

        <ul className="relative mt-6 flex flex-1 flex-col gap-2.5">
          {plan.features.map((f) => (
            <li
              key={f}
              className="flex items-start gap-2 text-[13.5px] leading-snug text-violet-50"
            >
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-violet-200" />
              {f}
            </li>
          ))}
        </ul>

        <button
          onClick={onSelect}
          disabled={disabled}
          className="relative mt-7 flex h-12 items-center justify-center rounded-full bg-white text-[14px] font-semibold text-violet-700 shadow-[0_10px_30px_-12px_rgba(255,255,255,0.5)] transition-all hover:-translate-y-0.5 hover:bg-violet-50 disabled:opacity-60"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <Spinner className="h-3.5 w-3.5" />
              Redirecting…
            </span>
          ) : (
            plan.cta
          )}
        </button>

        <p className="relative mt-3 text-center text-[11px] font-medium text-violet-200/80">
          30-day refund · No questions
        </p>
      </div>
    </div>
  );
}
